import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Clock, Shield, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const navigate = useNavigate();

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    // Only trigger when mouse leaves through the top of the viewport
    if (e.clientY <= 0 && !hasShown) {
      setIsVisible(true);
      setHasShown(true);
      // Store in session to prevent showing again this session
      sessionStorage.setItem('exitIntentShown', 'true');
    }
  }, [hasShown]);

  useEffect(() => {
    // Check if already shown this session
    if (sessionStorage.getItem('exitIntentShown')) {
      setHasShown(true);
      return;
    }

    // Add delay before enabling exit intent (don't trigger immediately)
    const timeout = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleStartAssessment = () => {
    setIsVisible(false);
    // Navigate to assessment section
    const element = document.getElementById('investment-assessment');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#investment-assessment');
    }
  };

  const handleViewPortal = () => {
    setIsVisible(false);
    navigate('/private-portal');
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-md z-50"
            onClick={handleClose}
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg mx-4 z-50"
          >
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-secondary via-card to-background border border-gold/30 shadow-2xl">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-muted/50 hover:bg-muted transition-colors"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>

              <div className="relative z-10 p-8 md:p-10">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-gold flex items-center justify-center">
                    <Crown className="h-6 w-6 text-cream" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gold uppercase tracking-wider">Exclusive Invitation</p>
                    <p className="text-sm text-muted-foreground">Limited Time Offer</p>
                  </div>
                </div>

                {/* Main content */}
                <h2 className="font-display text-2xl md:text-3xl text-foreground mb-4">
                  Before You Go...
                </h2>
                <p className="text-muted-foreground mb-8">
                  Complete our 2-minute Investment Assessment and receive exclusive access to our{' '}
                  <span className="text-gold font-medium">Private Client Portal</span> featuring 
                  off-market properties not listed publicly.
                </p>

                {/* Benefits */}
                <div className="grid grid-cols-2 gap-3 mb-8">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 border border-border/50">
                    <Clock className="h-4 w-4 text-gold flex-shrink-0" />
                    <span className="text-xs text-foreground">2-minute assessment</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 border border-border/50">
                    <Shield className="h-4 w-4 text-gold flex-shrink-0" />
                    <span className="text-xs text-foreground">Off-market access</span>
                  </div>
                </div>

                {/* CTAs */}
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleStartAssessment}
                    className="w-full bg-gradient-gold text-cream hover:opacity-90 h-12"
                    size="lg"
                  >
                    Start Assessment
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <Button
                    onClick={handleViewPortal}
                    variant="outline"
                    className="w-full border-gold/30 text-foreground hover:bg-gold/10 h-10"
                  >
                    Learn About Private Portal
                  </Button>
                </div>

                {/* Trust badge */}
                <p className="text-center text-xs text-muted-foreground mt-6">
                  Trusted by 200+ UHNWI investors worldwide
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
